import dayjs from 'dayjs';
import { ReactNode } from 'react';
import { IPositionResponse } from '../services/api/api.interfaces';

const MAX_ALLOWED_DIGITS = 8;

/**
 * Removes trailing zeros from a decimal string without converting to Number
 * (which would cause e-notation for small values).
 * Examples: "1.5000" → "1.5", "1.000" → "1", "100" → "100"
 */
export function removeTrailingZeros(str: string): string {
    return str.includes('.') ? str.replace(/\.?0+$/, '') : str;
}

function calculatePrecision(num?: number | string | null) {
    if (!num) {
        return 0;
    }
    // Convert the number to a string
    const numberStr = num + ''

    // Find the position of the decimal point
    const decimalIndex = numberStr.indexOf('.');

    // If there is no decimal point, return 0 (no decimal part needed)
    if (decimalIndex === -1) {
        return 0;
    }

    // Calculate the number of digits before the decimal point
    const digitsBeforeDecimal = decimalIndex !== -1 ? decimalIndex : numberStr.length;

    // If the total number of digits is 7 or more, return 0 (no decimal part should be shown)
    if (digitsBeforeDecimal >= MAX_ALLOWED_DIGITS) {
        return 0;
    }

    // Calculate the maximum precision allowed, which is 7 minus the number of digits before the decimal point
    const maxPrecisionAllowed = MAX_ALLOWED_DIGITS - digitsBeforeDecimal;

    return maxPrecisionAllowed;
}

export function formatCurrencyAmount(amount: number | string | null | undefined, currency: string, precision?: number): string {
    if (!precision && precision !== 0) {
        precision = calculatePrecision(amount);
    }
    amount = Number(amount);

    if(isNaN(amount)) {
        return '';
    }

    // Separate the amount into integer and decimal parts
    const parts = Math.abs(amount).toFixed(precision).split('.');
    const integerPart = parts[0];
    let decimalPart = parts.length > 1 ? '.' + parts[1] : '';

    if (decimalPart) {
        decimalPart = decimalPart.replace(/0+$/, '');
        if (decimalPart === '.') {
            decimalPart = '';
        }
    }

    // Format the integer part with spaces as thousand delimiters
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    // Determine if the currency is a symbol or a code
    const isSymbol = currency.length <= 3 && !/[a-zA-Z]/.test(currency);

    // Determine the sign based on the amount's positivity
    const sign = amount < 0 ? '-' : '';

    // Concatenate the parts based on whether the currency is a symbol or a code
    if (isSymbol) {
        // If currency is a symbol, it goes before the amount
        return `${sign}${currency}${formattedIntegerPart}${decimalPart}`;
    } else {
        // If currency is a code, it goes after the amount
        return `${sign}${formattedIntegerPart}${decimalPart} ${currency}`;
    }
}


export function formatPriceWithSign(amount: number | string | null | undefined, currency: string, precision?: number): string {
    if (!precision && precision !== 0) {
        precision = calculatePrecision(amount);
    }
    amount = Number(amount);

    if(isNaN(amount)) {
        return '';
    }

    // Separate the amount into integer and decimal parts
    const parts = formatPrice(Math.abs(amount)).split('.');
    const integerPart = parts[0];
    let decimalPart = parts.length > 1 ? '.' + parts[1] : '';

    if (decimalPart) {
        decimalPart = decimalPart.replace(/0+$/, '');
        if (decimalPart === '.') {
            decimalPart = '';
        }
    }

    // Format the integer part with spaces as thousand delimiters
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    // Determine if the currency is a symbol or a code
    const isSymbol = currency.length <= 3 && !/[a-zA-Z]/.test(currency);

    // Determine the sign based on the amount's positivity
    const sign = amount < 0 ? '-' : '';

    // Concatenate the parts based on whether the currency is a symbol or a code
    if (isSymbol) {
        // If currency is a symbol, it goes before the amount
        return `${sign}${currency}${formattedIntegerPart}${decimalPart}`;
    } else {
        // If currency is a code, it goes after the amount
        return `${sign}${formattedIntegerPart}${decimalPart} ${currency}`;
    }
}

export function getPricePrecision(_price: number | string | undefined) {
    if (_price === undefined || _price === null) {
        return 0
    }

    const price = Number(_price)
    if (price >= 100000) {
        return 0;
    }

    if (price >= 10000) {
        return 1;
    }

    if (price >= 100) {
        return 2;
    }

    if (price >= 1) {
        return 3;
    }

    if (price >= 0.001) {
        return 5;
    }

    return 8;
}
export function formatPrice(_price: number | string | undefined) {
    if (_price === undefined || _price === null) {
        return ''
    }

    const price = Number(_price)
    return price.toFixed(getPricePrecision(_price));
}

export function getPositionNameWithColor(position: IPositionResponse): ReactNode {
    const { baseAsset, isShort } = position;
    const direction = isShort ? 'Short' : 'Long';
    return (
        <span className={isShort ? 'nereus-color-status-warning' : 'nereus-color-main'}>
            {baseAsset} {direction}
    </span>
    );
}

export function getFullDate(date?: string | null) {
    if (!date) {
        return '';
    }
    return dayjs(date).format('D MMMM YYYY, HH:mm');
}

export function compactTxHash(txHash: string): string {
    // Validate input is a string and has sufficient length
    if (typeof txHash !== 'string' || txHash.length < 15) {
        return '0x';
    }

    // Define the number of characters to show from the start and end
    const startChars = 6;
    const endChars = 4;

    // Extract the start and end parts of the hash
    const start = txHash.slice(0, startChars);
    const end = txHash.slice(-endChars);

    // Return the compact version of the hash
    return `${start}...${end}`;
}